// @flow
import { Box, Stack, Typography } from '@mui/material';
import { keycloak } from '@coverright/shared/keycloak';
import { AiBadge, isAgentManager, NoData, Portal, useQaByAgentQuery } from '../../../shared';
import { AgentSelector } from '../../../features';
import { Preloader } from '@coverright/features';
import moment from 'moment';
import { QaScoreRing, ScoreText, scoreColor } from '../ui/qa-shared';
import { QaTrendChart } from '../ui/qa-trend-chart';
import { useCallDrawer } from '../ui/call-drawer-by-aircall';

type Props = {
  agentId?: string;
  onAgentChange?: (agentId?: string) => void;
};

export const MyScorecard = ({ agentId, onAgentChange }: Props) => {
  const isManager = isAgentManager();
  const currentUserId = keycloak?.tokenParsed?.sub as string | undefined;
  // Managers can view any agent (controlled from the page, e.g. Team Overview click);
  // agents are locked to their own scorecard.
  const selectedAgentId = agentId ?? currentUserId;

  return (
    <Stack gap={3}>
      {isManager && (
        <Box sx={{ width: 240 }}>
          <AgentSelector value={selectedAgentId} onChange={value => onAgentChange?.(value || undefined)} />
        </Box>
      )}
      <Scorecard agentId={selectedAgentId} />
    </Stack>
  );
};

const Scorecard = ({ agentId }: { agentId?: string }) => {
  const { data, loading } = useQaByAgentQuery({
    variables: { agentId },
    skip: !agentId,
    fetchPolicy: 'cache-and-network',
  });
  const { open } = useCallDrawer();

  if (loading && !data) return <Preloader sx={{ height: 300 }} />;
  const report = data?.qaByAgent;
  if (!report) return <NoData>No QA data for this agent.</NoData>;

  const { summary, pointsLostByCategory, mostFailedQuestions, recentCalls } = report;

  return (
    <Stack gap={3}>
      {/* Gauge + stats + trend */}
      <Stack direction={'row'} gap={3} flexWrap={'wrap'}>
        <Portal p={3} sx={{ alignItems: 'center', justifyContent: 'center', minWidth: 220 }}>
          <Stack alignItems={'center'} gap={1.5}>
            <QaScoreRing score={summary.averageScore} />
            <AiBadge label={'AI Scored'} />
          </Stack>
        </Portal>

        <Stack flex={1} gap={2} minWidth={280} sx={{ minWidth: 0 }}>
          <Stack direction={'row'} gap={2} flexWrap={'wrap'}>
            <Stat label={'Calls scored'} value={summary.callsScored} />
            <Stat label={'Enrollment calls'} value={summary.enrollmentCalls} />
            <Stat label={'Best score'} value={summary.bestScore} score />
            <Stat label={'Worst score'} value={summary.worstScore} score />
          </Stack>
          <Portal p={2} flex={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Stack direction={'row'} alignItems={'center'} mb={1}>
              <Typography variant={'subtitle1'} flex={1}>Score trend</Typography>
              <Typography variant={'caption'} color={'text.secondary'}>Per ISO week</Typography>
            </Stack>
            {summary.trend.length
              ? <QaTrendChart points={summary.trend} fill color={'#0DBC83'} />
              : <NoData height={120}>No trend yet</NoData>}
          </Portal>
        </Stack>
      </Stack>

      {/* Category bars + failed questions */}
      <Stack direction={'row'} gap={3} flexWrap={'wrap'} alignItems={'stretch'}>
        <Portal p={2} flex={1.3} minWidth={320} sx={{ justifyContent: 'space-between' }}>
          <Typography variant={'subtitle1'} mb={2}>Points lost by category</Typography>
          <Stack gap={3} flex={1} justifyContent={'center'}>
            {(() => {
              // Bars sized relative to the worst category (no max-points field from backend yet).
              const maxLost = Math.max(1, ...pointsLostByCategory.map(c => c.pointsLost));
              return pointsLostByCategory.map(c => {
                const pct = Math.min(100, (c.pointsLost / maxLost) * 100);
                const color = c.pointsLost === 0 ? '#0DBC83' : c.pointsLost >= maxLost * 0.66 ? '#F14E42' : '#EB9C15';
                return (
                  <Box key={c.category}>
                    <Stack direction={'row'} justifyContent={'space-between'} mb={0.5}>
                      <Typography variant={'body2'}>{c.category}</Typography>
                      <Typography variant={'caption'} color={'text.secondary'}>{c.pointsLost} pts lost</Typography>
                    </Stack>
                    <Box sx={{ height: 8, bgcolor: '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
                      <Box sx={{ height: 1, width: `${pct}%`, bgcolor: color, borderRadius: 4 }} />
                    </Box>
                  </Box>
                );
              });
            })()}
          </Stack>
        </Portal>

        <Portal p={2} flex={1} minWidth={320}>
          <Typography variant={'subtitle1'} mb={2}>Most failed questions</Typography>
          {mostFailedQuestions.length ? (
            <Stack gap={1} sx={{ maxHeight: 220, overflowY: 'auto' }}>
              <Stack direction={'row'} sx={{ position: 'sticky', top: 0, bgcolor: '#fff', pb: 0.5 }}>
                <Typography variant={'caption'} color={'text.secondary'} flex={1}>Question</Typography>
                <Typography variant={'caption'} color={'text.secondary'} width={60} textAlign={'right'}>Failed</Typography>
                <Typography variant={'caption'} color={'text.secondary'} width={60} textAlign={'right'}>Fail %</Typography>
              </Stack>
              {mostFailedQuestions.map(q => (
                <Stack key={q.questionId} direction={'row'} alignItems={'center'}>
                  <Typography variant={'body2'} flex={1}>
                    <Box component={'span'} sx={{ color: '#9CA3AF', fontWeight: 600, mr: 0.75 }}>{q.questionId}</Box>
                    {q.question}
                  </Typography>
                  <Typography variant={'body2'} width={60} textAlign={'right'}>{q.failCount}/{q.applicableCount}</Typography>
                  <Typography variant={'body2'} width={60} textAlign={'right'} sx={{ color: scoreColor(100 - q.failRate), fontWeight: 600 }}>
                    {Math.round(q.failRate)}%
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : <NoData height={100}>No failed questions</NoData>}
        </Portal>
      </Stack>

      {/* Recent calls */}
      <Portal p={2}>
        <Stack direction={'row'} alignItems={'center'} mb={2}>
          <Typography variant={'subtitle1'} flex={1}>Recent calls</Typography>
          <Typography variant={'caption'} color={'text.secondary'}>Row click opens call QA</Typography>
        </Stack>
        {recentCalls.length ? (
          <Stack>
            <Stack direction={'row'} sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
              <Typography variant={'caption'} color={'text.secondary'} flex={1}>Client</Typography>
              <Typography variant={'caption'} color={'text.secondary'} width={150}>Date</Typography>
              <Typography variant={'caption'} color={'text.secondary'} width={140}>Disposition</Typography>
              <Typography variant={'caption'} color={'text.secondary'} width={70} textAlign={'right'}>Score</Typography>
            </Stack>
            {recentCalls.map(c => (
              <Stack key={c.airCallId} direction={'row'} alignItems={'center'} sx={{ py: 1, cursor: 'pointer', '&:hover': { bgcolor: '#f6fafb' } }}
                     onClick={() => open(c.airCallId)}>
                <Typography variant={'body2'} flex={1}>{c.clientName || '-'}</Typography>
                <Typography variant={'body2'} width={150}>{c.callDate ? moment(c.callDate).format('L LT') : '-'}</Typography>
                <Typography variant={'body2'} width={140} sx={{ textTransform: 'capitalize' }}>{c.dispositionClass}</Typography>
                <Box width={70} textAlign={'right'}><ScoreText score={c.effectiveScore} variant={'body2'} /></Box>
              </Stack>
            ))}
          </Stack>
        ) : <NoData height={100}>No recent calls</NoData>}
      </Portal>
    </Stack>
  );
};

const Stat = ({ label, value, score }: { label: string; value?: number | null; score?: boolean }) => (
  <Portal p={2} sx={{ minWidth: 130, flex: 1 }}>
    <Typography variant={'caption'} color={'text.secondary'}>{label}</Typography>
    {score
      ? <ScoreText score={value} variant={'h3'} />
      : <Typography variant={'h3'}>{value ?? '-'}</Typography>}
  </Portal>
);